import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
    FiCreditCard,
    FiDollarSign,
    FiFileText,
    FiBriefcase,
    FiPlus,
    FiArrowRight,
    FiTrendingUp,
    FiCheckSquare,
    FiHome,
    FiGlobe,
    FiCheck,
} from "react-icons/fi";
import { useApp } from "../../../context/AppContext";
import api from "../../../services/api";
import reconciliationService from "../../../services/reconciliationService";
import CollegeLogoBadge from "../../../components/common/CollegeLogoBadge";
import collegeService from "../../../services/collegeService";

export default function AccountantDashboard() {
    const {
        user,
        collegeName,
        allColleges,
        selectedCollegeId,
        selectCollege,
        collegeData,
        academicYear,
    } = useApp();
    const navigate = useNavigate();

    const [stats, setStats] = useState({
        totalCollected: 0,
        totalPending: 0,
        todayCollection: 0,
        receiptsCount: 0,
    });
    const [recentReceipts, setRecentReceipts] = useState([]);
    const [reconStats, setReconStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [localColleges, setLocalColleges] = useState([]);

    useEffect(() => {
        if (allColleges && allColleges.length > 0) {
            setLocalColleges(allColleges);
        } else {
            collegeService.getAll().then((res) => {
                const list = Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.data) ? res.data.data : []);
                if (Array.isArray(list) && list.length > 0) {
                    setLocalColleges(list);
                }
            }).catch(() => {});
        }
    }, [allColleges]);

    const activeColleges = (allColleges && allColleges.length > 0) ? allColleges : localColleges;

    useEffect(() => {
        let isMounted = true;

        const fetchDashboardData = async () => {
            setLoading(true);
            try {
                const baseParams = {};
                if (selectedCollegeId && selectedCollegeId !== "all") {
                    baseParams.college = selectedCollegeId;
                }
                const [receiptsRes, statsRes, reconRes] = await Promise.allSettled([
                    api.get("/fees/receipts", { params: { limit: 6, ...baseParams } }),
                    api.get("/fees/year-summary", { params: baseParams }),
                    reconciliationService.getDashboardSummaryStats(baseParams),
                ]);

                if (!isMounted) return;

                if (reconRes.status === "fulfilled" && reconRes.value.data) {
                    setReconStats(reconRes.value.data);
                }

                if (statsRes.status === "fulfilled") {
                    const s = statsRes.value.data || statsRes.value;
                    setStats({
                        totalCollected: s.totalCollected || 0,
                        totalPending: s.totalPending || 0,
                        todayCollection: s.todayCollection || 0,
                        receiptsCount: s.receiptsCount || 0,
                    });
                }

                if (receiptsRes.status === "fulfilled") {
                    const data = receiptsRes.value.data || receiptsRes.value;
                    const list = Array.isArray(data) ? data : data.data || [];
                    setRecentReceipts(list);
                }
            } catch (err) {
                if (isMounted) {
                    toast.error(err.message || "Failed to load accountant dashboard");
                }
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        fetchDashboardData();

        return () => {
            isMounted = false;
        };
    }, [selectedCollegeId, academicYear]);

    const isAllCampuses = !selectedCollegeId || selectedCollegeId === "all";
    const currentActiveName = isAllCampuses ? "All Campuses (Consolidated Group)" : (collegeData?.name || collegeName);

    return (
        <div>
            {/* ── Page Header ── */}
            <div className="page-header" style={{ flexWrap: "wrap", gap: "1rem", marginBottom: "1.25rem", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.85rem" }}>
                    <CollegeLogoBadge size={52} />
                    <div>
                        <div style={{ fontSize: "11px", fontWeight: 700, color: "#003b67", letterSpacing: "0.04em", textTransform: "uppercase" }}>
                            {currentActiveName}
                        </div>
                        <h1 className="page-title" style={{ margin: "2px 0 0 0" }}>Welcome back, {user?.displayName || user?.username} 💰</h1>
                        <p className="page-subtitle" style={{ margin: "2px 0 0 0" }}>Centralized Finance Desk &amp; Multi-Campus Accounts Operations</p>
                    </div>
                </div>
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                    <button
                        className="btn btn-outline"
                        onClick={() => navigate("/accountant/fees")}
                        style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}
                    >
                        <FiCreditCard size={14} /> Fee Dashboard
                    </button>
                    <button
                        className="btn btn-outline"
                        onClick={() => navigate("/accountant/fees/pending")}
                        style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}
                    >
                        <FiDollarSign size={14} /> Pending Dues
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={() => navigate("/accountant/fees/new")}
                        style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}
                    >
                        <FiPlus size={14} /> Collect Fees
                    </button>
                </div>
            </div>

            {/* ── Multi-College Campus Switcher Bar ── */}
            <div
                className="card"
                style={{
                    padding: "1rem 1.25rem",
                    marginBottom: "1.5rem",
                    background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                    border: "1px solid #cbd5e1",
                    borderRadius: "12px",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
                }}
            >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "0.75rem", marginBottom: "0.75rem" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <div style={{ width: 28, height: 28, borderRadius: 8, background: "#003b67", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff" }}>
                            <FiHome size={14} />
                        </div>
                        <div>
                            <span style={{ fontSize: "11px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                College / Campus Selector
                            </span>
                            <div style={{ fontSize: "14px", fontWeight: 700, color: "#0f172a" }}>
                                {isAllCampuses ? "🌐 All Campuses (Group Consolidated View)" : `🏢 ${collegeData?.name || collegeName}`}
                            </div>
                        </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <span style={{ fontSize: "12px", color: "var(--text-muted)", fontWeight: 500 }}>
                            Quick Select:
                        </span>
                        <select
                            className="form-control"
                            style={{ minWidth: 220, fontSize: "13px", padding: "5px 10px", fontWeight: 600, borderColor: "#94a3b8" }}
                            value={selectedCollegeId || "all"}
                            onChange={(e) => selectCollege(e.target.value)}
                        >
                            <option value="all">🌐 All Campuses (Group Overview)</option>
                            {activeColleges.map((c) => (
                                <option key={c._id} value={c._id}>
                                    {c.name} ({c.code})
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Campus Quick-Pills */}
                <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", alignItems: "center" }}>
                    <button
                        type="button"
                        onClick={() => selectCollege("all")}
                        style={{
                            padding: "6px 14px",
                            borderRadius: "20px",
                            fontSize: "12px",
                            fontWeight: isAllCampuses ? 700 : 500,
                            cursor: "pointer",
                            transition: "all 0.15s ease",
                            border: isAllCampuses ? "2px solid #003b67" : "1px solid #cbd5e1",
                            background: isAllCampuses ? "#003b67" : "#ffffff",
                            color: isAllCampuses ? "#ffffff" : "#334155",
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                        }}
                    >
                        <FiGlobe size={13} /> All Campuses ({activeColleges.length})
                        {isAllCampuses && <FiCheck size={13} />}
                    </button>

                    {activeColleges.map((c) => {
                        const isSelected = String(c._id) === String(selectedCollegeId);
                        return (
                            <button
                                key={c._id}
                                type="button"
                                onClick={() => selectCollege(c)}
                                style={{
                                    padding: "6px 14px",
                                    borderRadius: "20px",
                                    fontSize: "12px",
                                    fontWeight: isSelected ? 700 : 500,
                                    cursor: "pointer",
                                    transition: "all 0.15s ease",
                                    border: isSelected ? "2px solid #0284c7" : "1px solid #cbd5e1",
                                    background: isSelected ? "#0284c7" : "#ffffff",
                                    color: isSelected ? "#ffffff" : "#334155",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "5px",
                                }}
                            >
                                <span>{c.code || c.name}</span>
                                {isSelected && <FiCheck size={13} />}
                            </button>
                        );
                    })}
                </div>
            </div>

            {loading ? (
                <div className="loading-center" style={{ minHeight: "240px" }}>
                    <div className="spinner" />
                    Loading accounts data for {currentActiveName}…
                </div>
            ) : (
                <>
                    {/* ── KPI Metric Cards ── */}
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
                            gap: "1rem",
                            marginBottom: "1.5rem",
                        }}
                    >
                        <div
                            className="card"
                            style={{ padding: "1.1rem", cursor: "pointer", transition: "transform 0.15s" }}
                            onClick={() => navigate("/accountant/fees/receipts")}
                        >
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                                <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)", letterSpacing: "0.05em" }}>
                                    Today's Collection
                                </span>
                                <div style={{ width: 32, height: 32, borderRadius: 8, background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <FiTrendingUp size={15} color="#16a34a" />
                                </div>
                            </div>
                            <div style={{ fontSize: 24, fontWeight: 900, color: "#16a34a" }}>
                                ₹{stats.todayCollection.toLocaleString("en-IN")}
                            </div>
                            <div style={{ fontSize: 11.5, color: "#16a34a", marginTop: 4, display: "flex", alignItems: "center", gap: 3 }}>
                                Daily Total <FiArrowRight size={10} />
                            </div>
                        </div>

                        <div
                            className="card"
                            style={{ padding: "1.1rem", cursor: "pointer", transition: "transform 0.15s" }}
                            onClick={() => navigate("/accountant/fees")}
                        >
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                                <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)", letterSpacing: "0.05em" }}>
                                    Total Collections
                                </span>
                                <div style={{ width: 32, height: 32, borderRadius: 8, background: "#e0f2fe", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <FiCreditCard size={15} color="#0284c7" />
                                </div>
                            </div>
                            <div style={{ fontSize: 24, fontWeight: 900, color: "var(--text)" }}>
                                ₹{stats.totalCollected.toLocaleString("en-IN")}
                            </div>
                            <div style={{ fontSize: 11.5, color: "#0284c7", marginTop: 4, display: "flex", alignItems: "center", gap: 3 }}>
                                Fee Analytics <FiArrowRight size={10} />
                            </div>
                        </div>

                        <div
                            className="card"
                            style={{ padding: "1.1rem", cursor: "pointer", transition: "transform 0.15s" }}
                            onClick={() => navigate("/accountant/fees/pending")}
                        >
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                                <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)", letterSpacing: "0.05em" }}>
                                    Pending Student Fees
                                </span>
                                <div style={{ width: 32, height: 32, borderRadius: 8, background: "#fee2e2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <FiDollarSign size={15} color="#dc2626" />
                                </div>
                            </div>
                            <div style={{ fontSize: 24, fontWeight: 900, color: stats.totalPending > 0 ? "#dc2626" : "#16a34a" }}>
                                ₹{stats.totalPending.toLocaleString("en-IN")}
                            </div>
                            <div style={{ fontSize: 11.5, color: stats.totalPending > 0 ? "#dc2626" : "var(--text-muted)", marginTop: 4 }}>
                                {stats.totalPending > 0 ? "Pending recovery" : "All cleared ✓"}
                            </div>
                        </div>

                        <div
                            className="card"
                            style={{ padding: "1.1rem", cursor: "pointer", transition: "transform 0.15s" }}
                            onClick={() => navigate("/accountant/payroll")}
                        >
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                                <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)", letterSpacing: "0.05em" }}>
                                    Payroll Management
                                </span>
                                <div style={{ width: 32, height: 32, borderRadius: 8, background: "#ede9fe", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <FiBriefcase size={15} color="#7c3aed" />
                                </div>
                            </div>
                            <div style={{ fontSize: 24, fontWeight: 900, color: "var(--text)" }}>
                                Salaries
                            </div>
                            <div style={{ fontSize: 11.5, color: "#7c3aed", marginTop: 4, display: "flex", alignItems: "center", gap: 3 }}>
                                Disbursements <FiArrowRight size={10} />
                            </div>
                        </div>
                    </div>

                    {/* ── Cash Vault & Bank Reconciliation Desk ── */}
                    <div
                        className="card"
                        style={{
                            padding: "1.25rem",
                            marginBottom: "1.5rem",
                            background: "linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%)",
                            border: "1px solid #bbf7d0",
                        }}
                    >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "1rem" }}>
                            <div>
                                <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 700, color: "#166534", fontSize: 14 }}>
                                    <FiCheckSquare size={16} /> Cash Vault &amp; Bank Reconciliation Desk
                                </div>
                                <p style={{ margin: "4px 0 0", fontSize: 12.5, color: "var(--text-muted)" }}>
                                    Cash in Vault: <strong>₹{Number(reconStats?.cashInAccountantVault || 0).toLocaleString("en-IN")}</strong> ({reconStats?.pendingDepositsCount || 0} batches) &nbsp;|&nbsp; Handovers Pending Verification: <strong>{reconStats?.handoverSummary?.submittedCount || 0}</strong>
                                </p>
                            </div>
                            <button
                                className="btn btn-primary"
                                onClick={() => navigate("/accountant/reconciliation")}
                                style={{ display: "flex", alignItems: "center", gap: 6, padding: "0.55rem 1rem", fontSize: 13 }}
                            >
                                Go to Cash &amp; Bank Desk <FiArrowRight size={13} />
                            </button>
                        </div>
                    </div>

                    {/* ── Recent Transactions ── */}
                    <div className="card">
                        <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontWeight: 700 }}>
                                <FiFileText size={14} color="#059669" /> Recent Fee Receipts
                                {isAllCampuses && (
                                    <span style={{ fontSize: 11, background: "#f1f5f9", color: "#64748b", padding: "1px 6px", borderRadius: 99, fontWeight: 600 }}>
                                        Cross-Campus
                                    </span>
                                )}
                            </span>
                            <button
                                className="btn btn-outline btn-sm"
                                onClick={() => navigate("/accountant/fees/receipts")}
                                style={{ display: "flex", alignItems: "center", gap: "0.3rem", fontSize: 12 }}
                            >
                                View All <FiArrowRight size={11} />
                            </button>
                        </div>
                        <div className="card-body" style={{ padding: 0 }}>
                            {recentReceipts.length === 0 ? (
                                <div style={{ padding: "1.5rem", textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>
                                    No fee receipt records found for {currentActiveName}.
                                </div>
                            ) : (
                                recentReceipts.map((item, idx) => (
                                    <div
                                        key={item._id || idx}
                                        style={{
                                            padding: "0.75rem 1rem",
                                            borderBottom: "1px solid var(--border)",
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            fontSize: 13,
                                        }}
                                    >
                                        <div>
                                            <div style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                                                <span>{item.student?.fullName || item.studentName || "Student"}</span>
                                                {item.college?.code && (
                                                    <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 4, background: "#e0f2fe", color: "#0369a1" }}>
                                                        {item.college.code}
                                                    </span>
                                                )}
                                            </div>
                                            <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                                                Receipt #{item.receiptNo || "—"} · Date: {item.paymentDate ? item.paymentDate.slice(0, 10) : "—"} · Mode: {item.paymentMode || "Cash"}
                                            </div>
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                                            <span style={{ fontWeight: 700, color: "#16a34a" }}>
                                                ₹{(item.amountPaid || item.amount || 0).toLocaleString("en-IN")}
                                            </span>
                                            <button
                                                className="btn btn-outline btn-sm"
                                                onClick={() => navigate(`/accountant/fees/receipts/${item._id}/print`)}
                                                style={{ fontSize: 11, padding: "2px 8px" }}
                                            >
                                                Print
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}